/**
 * Does a workspace carry a paid entitlement?
 *
 * The rule is `UserDataContext.hasWorkspaceActiveSubscription`'s, extracted so it
 * can also be applied to a workspace the logged-in user is NOT in: an admin
 * ordering on behalf must be judged on the CLIENT's selected workspace, never on
 * their own. All four clauses matter:
 *
 *   - a PERSONAL plan is the free tier, whatever its status;
 *   - only NEW / ACTIVE / IN_GRACE_PERIOD count as paying (`ActiveSubscriptionStatuses`);
 *   - non-members inherit nothing from a workspace they merely see;
 *   - only a FULL or SPONSORED seat unlocks paid features, even inside a paid
 *     workspace — seat type is orthogonal to the plan. A missing or unknown seat
 *     type fails closed.
 *
 * Legacy's own admin path was looser: it accepted ANY active non-personal
 * workspace the target belonged to (`TargetOrderUser.context.ts`), so a client
 * with one paid workspace got paid features while ordering into a different,
 * free one. The product rule is the SELECTED workspace, so that is not ported.
 */

import { ActiveSubscriptionStatuses, SubscriptionPlan, WorkspaceSeatType } from 'models/user'
import type { SubscriptionStatus } from 'models/user'

/** The subset of a user-workspace this decision reads. */
export interface EntitlementWorkspace {
  plan: SubscriptionPlan
  status: SubscriptionStatus
  isMember: boolean
  seatType: WorkspaceSeatType
}

/**
 * `true` when the workspace is a paying one AND the user's seat in it can use
 * paid features.
 *
 * @example
 * workspaceHasPaidEntitlement({ plan: 'PRO', status: 'ACTIVE', isMember: true, seatType: 'FULL' })  // true
 * workspaceHasPaidEntitlement({ plan: 'PRO', status: 'ACTIVE', isMember: true, seatType: 'RESTRICTED' })  // false
 */
export const workspaceHasPaidEntitlement = (workspace: EntitlementWorkspace | null | undefined): boolean => {
  if (!workspace) return false
  if (!workspace.isMember) return false
  if (workspace.plan === SubscriptionPlan.PERSONAL) return false
  if (!ActiveSubscriptionStatuses.has(workspace.status)) return false
  return workspace.seatType === WorkspaceSeatType.FULL || workspace.seatType === WorkspaceSeatType.SPONSORED
}
